import { useEffect, useMemo, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { CallBackProps, STATUS, Step } from 'react-joyride'

import { guideTooltipDelay } from 'entities/tour/types'
import { setSettingForApp } from 'entities/user/module/app-settings'
import userStore from 'entities/user/module/user-store'

interface UseTourGuideProps {
  steps: Step[]
  cookieKey: string
  localeKeys: {
    back?: string
    close?: string
    last: string
    next?: string
    skip?: string
    nextLabelWithProgress?: string
  }
}

export const useGuideTooltip = ({
  steps,
  cookieKey,
  localeKeys,
}: UseTourGuideProps) => {
  const { t } = useTranslation()
  const [run, setRun] = useState(false)

  const seen = userStore.getClientStateString(cookieKey)

  const locale = useMemo(() => {
    const result: any = {}

    if (localeKeys.back) result.back = t(localeKeys.back)
    if (localeKeys.close) result.close = t(localeKeys.close)
    if (localeKeys.last) result.last = t(localeKeys.last)
    if (localeKeys.next) result.next = t(localeKeys.next)
    if (localeKeys.skip) result.skip = t(localeKeys.skip)
    if (localeKeys.nextLabelWithProgress)
      result.nextLabelWithProgress = t(localeKeys.nextLabelWithProgress)

    return result
  }, [t, localeKeys])

  const handleCallback = (data: CallBackProps) => {
    const { status } = data

    if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
      void setSettingForApp({ key: cookieKey, value: 'seen' })
      setRun(false)
    }
  }

  useEffect(() => {
    let timeoutId: NodeJS.Timeout

    if (steps.length > 0 && !seen) {
      timeoutId = setTimeout(() => {
        setRun(true)
      }, guideTooltipDelay)
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId)
      }
    }
  }, [seen, steps])

  useEffect(() => {
    if (run) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'auto'
    }

    return () => {
      document.body.style.overflow = 'auto'
    }
  }, [run])

  return {
    run,
    locale,
    handleCallback,
  }
}
